import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useGameStore } from "@/stores/gameStore";
import { useGameGroupStore } from "@/stores/game/gameGroupStore";

/**
 * Who is looking at the game screen and which groups they may see.
 * Used by the live game (PlayGameView), the replay and the shared GameScreen,
 * so all of them follow the same host / moderator / player rules.
 */
export default function useGameViewer() {
  const gameStore = useGameStore();
  const gameGroupStore = useGameGroupStore();
  const { game, isHost, user } = storeToRefs(gameStore);
  const { groups, groupsWithSaboteur } = storeToRefs(gameGroupStore);

  const currentUserPlayer = computed(() =>
    (game.value?.players ?? []).find((p) => Number(p.id) === Number(user.value?.id))
  );
  const isModerator = computed(() => currentUserPlayer.value?.role === "moderator");
  const moderatorGroupIds = computed(() => gameGroupStore.moderatorGroupIds ?? []);
  const canUseHostView = computed(() => isHost.value || isModerator.value);
  const isOwnGroupModerator = computed(() => isModerator.value && moderatorGroupIds.value.length > 0);
  const canAccessCurrentGroupHostFeatures = computed(() => isHost.value || isOwnGroupModerator.value);

  // The host sees every group, a moderator only the groups they moderate, a player none.
  function scopeToViewer(list) {
    if (isHost.value) return list ?? [];
    if (isModerator.value) {
      return (list ?? []).filter((group) => moderatorGroupIds.value.includes(Number(group.id)));
    }
    return [];
  }

  const scopedGroups = computed(() => scopeToViewer(groups.value));
  const saboteurGroupsForViewer = computed(() => scopeToViewer(groupsWithSaboteur.value));

  const displayGroupsWithPlayers = computed(() => {
    const sourceGroups = isModerator.value ? scopedGroups.value : groups.value;
    const players = game.value?.players ?? [];
    return (sourceGroups ?? []).map((group) => ({
      ...group,
      players: players.filter((p) => Number(p.groupId) === Number(group.id)),
    }));
  });

  const focusedPrelimVoteGroup = computed(() => {
    const groupList = displayGroupsWithPlayers.value;
    if (!groupList.length) return null;
    const currentId = Number(gameGroupStore.currentGroupId);
    const group = groupList.find((g) => Number(g.id) === currentId) ?? groupList[0];
    const players = (group.players ?? []).filter((p) => p.role === "player" || p.role === "saboteur");
    return {
      ...group,
      totalPlayersCount: players.length,
      votesCastedCount: players.filter((p) => p.prelimVotedAt).length,
    };
  });

  return {
    currentUserPlayer,
    isModerator,
    moderatorGroupIds,
    canUseHostView,
    isOwnGroupModerator,
    canAccessCurrentGroupHostFeatures,
    scopeToViewer,
    scopedGroups,
    saboteurGroupsForViewer,
    displayGroupsWithPlayers,
    focusedPrelimVoteGroup,
  };
}
